import React, { useState, useEffect } from 'react';
import { Mail, Eye, Reply, Trash2, CheckCircle, Clock, Search, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

interface Contact {
  strGUID: string;
  strName: string;
  strEmail: string;
  strSubject: string;
  strMessage: string;
  strPhone: string;
  bolIsRead: boolean;
  bolIsReplied: boolean;
  dtCreatedDate: string;
  dtReadDate?: string;
  dtRepliedDate?: string;
  strAdminReplay?: string;
  strRepliedBy?: string;
  strSource?: string;
  strUserIP: string;
  strUserAgent: string;
}

interface ContactReplyDTO {
  strContactGUID: string;
  strReplyMessage: string;
  strRepliedBy: string;
}

class ContactService {
  private baseUrl = `${process.env.REACT_APP_API_BASE_URL || 'https://localhost:7222/api'}/contact`;

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getContacts(): Promise<{ contacts: Contact[]; unreadCount: number; totalCount: number }> {
    const response = await fetch(this.baseUrl, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async markAsRead(contactId: string, readBy: string): Promise<void> {
    const requestBody = { 
      strContactGUID: contactId, 
      strReadBy: readBy 
    };
    
    const response = await fetch(`${this.baseUrl}/${contactId}/read`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
  }

  async replyToContact(replyDto: ContactReplyDTO): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${replyDto.strContactGUID}/reply`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        strContactGUID: replyDto.strContactGUID,
        strReplyMessage: replyDto.strReplyMessage,
        strRepliedBy: replyDto.strRepliedBy
      })
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async deleteContact(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }
}

const contactService = new ContactService();

const ContactManagement: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'replied' | 'unreplied'>('all');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await contactService.getContacts();
      setContacts(data.contacts);
      setUnreadCount(data.unreadCount);
      setTotalCount(data.totalCount);
    } catch (error) {
      // Error loading contacts
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (contact: Contact) => {
    try {
      await contactService.markAsRead(contact.strGUID, 'Admin');
      await loadContacts(); // Refresh the list
    } catch (error) {
      // Error marking as read
    }
  };

  const handleReply = async () => {
    if (!selectedContact || !replyMessage.trim()) return;

    try {
      setReplying(true);
      await contactService.replyToContact({
        strContactGUID: selectedContact.strGUID,
        strReplyMessage: replyMessage.trim(),
        strRepliedBy: 'Admin'
      });
      setShowReplyModal(false);
      setReplyMessage('');
      setSelectedContact(null);
      await loadContacts(); // Refresh the list
    } catch (error) {
      // Error sending reply
    } finally {
      setReplying(false);
    }
  };

  const handleDelete = async (contact: Contact) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await contactService.deleteContact(contact.strGUID);
      await loadContacts(); // Refresh the list
    } catch (error) {
      // Error deleting contact
    }
  };

  const filteredContacts = contacts.filter(contact => {
    // Add null checks and provide default values for search fields
    const name = contact.strName || '';
    const email = contact.strEmail || '';
    const subject = contact.strSubject || '';
    const message = contact.strMessage || '';
    const searchLower = (searchTerm || '').toLowerCase();
    
    const matchesSearch = 
      name.toLowerCase().includes(searchLower) ||
      email.toLowerCase().includes(searchLower) ||
      subject.toLowerCase().includes(searchLower) ||
      message.toLowerCase().includes(searchLower);

    const matchesFilter = 
      filter === 'all' ||
      (filter === 'unread' && !contact.bolIsRead) ||
      (filter === 'replied' && contact.bolIsReplied) ||
      (filter === 'unreplied' && !contact.bolIsReplied);

    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string) => {
    try {
      // Try parsing as ISO string first
      let utcDate = new Date(dateString);
      
      if (isNaN(utcDate.getTime())) {
        // Direct parsing failed, try ISO string
        utcDate = new Date(dateString + 'Z'); // Add Z to make it UTC
      }
      
      // If still fails, try parsing as local date
      if (isNaN(utcDate.getTime())) {
        utcDate = new Date(dateString.replace(' ', 'T'));
      }
      
      // If all parsing methods fail
      if (isNaN(utcDate.getTime())) {
        return 'Invalid Date';
      }
      
      // Successfully parsed date
      const now = new Date();
      
      // Convert to Indian timezone (IST)
      const indianTime = utcDate.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      // Calculate time difference
      const timeDiff = now.getTime() - utcDate.getTime();
      const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));
      
      if (hoursDiff < 1) {
        return 'Just now';
      } else if (hoursDiff < 24) {
        return `${hoursDiff} hour${hoursDiff > 1 ? 's' : ''} ago`;
      } else {
        return indianTime + ' IST';
      }
    } catch (error) {
      // Error formatting date
      return 'Invalid Date';
    }
  };

  // Helper function to get relative time in Indian timezone
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      const utcDate = new Date(dateString);
      const now = new Date();
      
      // Calculate time difference in milliseconds
      const diffInMs = now.getTime() - utcDate.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      
      // For older dates, return the formatted date
      return formatDate(dateString);
    } catch (error) {
      return formatDate(dateString);
    }
  };

  // Manual Indian time calculation function for debugging
  const getIndianTimeManual = (dateString: string) => {
    if (!dateString) return 'No time';
    
    try {
      const utcDate = new Date(dateString);
      
      if (isNaN(utcDate.getTime())) {
        return 'Invalid date';
      }
      
      // Manual calculation: IST is UTC+5:30
      const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
      const istDate = new Date(utcDate.getTime() + istOffset);
      
      return istDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      // Error in manual IST calculation
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Contact Messages</h2>
            <p className="text-gray-600 dark:text-slate-400">Review and respond to customer inquiries</p>
            <div className="mt-2 text-xs text-gray-500 dark:text-slate-400">
              <span className="font-medium">Note:</span> All dates are displayed in <span className="font-semibold text-orange-600">Indian Standard Time (IST)</span>
              <button 
                onClick={() => setShowDebugInfo(!showDebugInfo)}
                className="ml-2 text-blue-600 hover:text-blue-800 underline cursor-pointer"
              >
                {showDebugInfo ? 'Hide' : 'Show'} debug info
              </button>
            </div>
            {showDebugInfo && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700 text-xs text-yellow-800 dark:text-yellow-200">
                <div className="font-medium mb-2">Debug Information:</div>
                <div className="space-y-1">
                  <div>• Current UTC: <span className="font-mono">{new Date().toISOString()}</span></div>
                  <div>• Current Local: <span className="font-mono">{new Date().toString()}</span></div>
                  <div>• Current IST (Auto): <span className="font-mono">{new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}</span></div>
                  <div>• Current IST (Manual): <span className="font-mono">{new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000).toISOString()}</span></div>
                  <div>• IST Offset: <span className="font-mono">UTC+5:30 (5.5 hours)</span></div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4 mt-4 sm:mt-0">
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-slate-700 px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-600">
              <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{unreadCount} unread</span>
            </div>
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-slate-700 px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-600">
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{totalCount} total</span>
            </div>
            <div className="flex items-center space-x-2 bg-orange-50 dark:bg-orange-900/20 px-4 py-2 rounded-lg border border-orange-200 dark:border-orange-700">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                <span className="font-semibold">Indian Time:</span> {new Date().toLocaleTimeString('en-US', {
                  timeZone: 'Asia/Kolkata',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })} IST
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <Card className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:focus:ring-slate-500 dark:focus:border-slate-500 transition-colors bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:focus:ring-slate-500 dark:focus:border-slate-500 transition-colors bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            >
              <option value="all">All Contacts</option>
              <option value="unread">Unread</option>
              <option value="replied">Replied</option>
              <option value="unreplied">Unreplied</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Contacts List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredContacts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {searchTerm || filter !== 'all' ? 'No matching contacts found' : 'No contact messages yet'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-md mx-auto">
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your search terms or filter criteria to find what you\'re looking for.' 
                : 'When visitors submit contact forms through your website, they will appear here for you to review and respond.'
              }
            </p>
            {(searchTerm || filter !== 'all') && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilter('all');
                }}
                className="mt-4"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex flex-col">
                      <span>Date & Time</span>
                      <span className="text-xs font-normal normal-case text-gray-400">
                        Indian Time (IST)
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContacts.map((contact) => (
                  <tr key={contact.strGUID} className={!contact.bolIsRead ? 'bg-gray-50 dark:bg-slate-700/50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {(contact.strName || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{contact.strName || 'Unknown'}</div>
                          <div className="text-sm text-gray-500 dark:text-slate-400">{contact.strEmail || 'No email'}</div>
                          {contact.strPhone && (
                            <div className="text-sm text-gray-500 dark:text-slate-400">{contact.strPhone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 font-medium">{contact.strSubject || 'No subject'}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{contact.strMessage || 'No message'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col">
                        <span className="font-medium text-blue-600">{formatDate(contact.dtCreatedDate)}</span>
                        <span className="text-xs text-orange-600">{getIndianTimeManual(contact.dtCreatedDate)}</span>
                        <span className="text-xs text-gray-400">{getRelativeTime(contact.dtCreatedDate)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {contact.bolIsRead ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500" />
                        )}
                        <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                          contact.bolIsReplied 
                            ? 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600' 
                            : contact.bolIsRead 
                            ? 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600' 
                            : 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600'
                        }`}>
                          {contact.bolIsReplied ? '✓ Replied' : contact.bolIsRead ? '✓ Read' : '⏳ Unread'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setSelectedContact(contact)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!contact.bolIsReplied && (
                          <button
                            onClick={() => {
                              setSelectedContact(contact);
                              setShowReplyModal(true);
                            }}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Reply"
                          >
                            <Reply className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(contact)}
                          className="text-red-600 hover:text-blue-900 p-1"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 md:w-3/4 lg:w-1/2 shadow-2xl rounded-xl bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Contact Details</h3>
                    <p className="text-sm text-gray-500">Message from {selectedContact.strName || 'Unknown'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Information</label>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-900"><span className="font-medium">Name:</span> {selectedContact.strName || 'Unknown'}</p>
                      <p className="text-sm text-gray-900"><span className="font-medium">Email:</span> {selectedContact.strEmail || 'No email'}</p>
                      {selectedContact.strPhone && (
                        <p className="text-sm text-gray-900"><span className="font-medium">Phone:</span> {selectedContact.strPhone}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Message Details</label>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-900"><span className="font-medium">Subject:</span> {selectedContact.strSubject || 'No subject'}</p>
                      <p className="text-sm text-gray-900"><span className="font-medium">Submitted:</span> {formatDate(selectedContact.dtCreatedDate)}</p>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">Status:</span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          selectedContact.bolIsReplied 
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                            : selectedContact.bolIsRead 
                            ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                            : 'bg-amber-100 text-amber-800 border border-amber-200'
                        }`}>
                          {selectedContact.bolIsReplied ? '✓ Replied' : selectedContact.bolIsRead ? '✓ Read' : '⏳ Unread'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message Content</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedContact.strMessage || 'No message content'}</p>
                </div>
                
                {selectedContact.strAdminReplay && (
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Reply</label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed mb-2">{selectedContact.strAdminReplay}</p>
                    <p className="text-xs text-gray-500">
                      Replied by {selectedContact.strRepliedBy} on {selectedContact.dtRepliedDate && formatDate(selectedContact.dtRepliedDate)}
                    </p>
                  </div>
                )}
                
                <div className="flex items-center space-x-4 pt-4 border-t">
                  <Button
                    onClick={() => handleMarkAsRead(selectedContact)}
                    disabled={selectedContact.bolIsRead}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 ${
                      selectedContact.bolIsRead 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {selectedContact.bolIsRead ? '✓ Already Read' : 'Mark as Read'}
                  </Button>
                  
                  {!selectedContact.bolIsReplied && (
                    <Button
                      onClick={() => setShowReplyModal(true)}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors duration-200"
                    >
                      <Reply className="h-4 w-4 mr-2" />
                      Send Reply
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedContact && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 md:w-1/2 shadow-2xl rounded-xl bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-green-100 rounded-full flex items-center justify-center">
                    <Reply className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Send Reply</h3>
                    <p className="text-sm text-gray-500">Respond to {selectedContact.strName || 'Unknown'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowReplyModal(false);
                    setReplyMessage('');
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Original Message</label>
                  <p className="text-sm text-gray-900 mb-2"><span className="font-medium">Subject:</span> {selectedContact.strSubject || 'No subject'}</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedContact.strMessage || 'No message content'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Your Reply Message</label>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none transition-colors duration-200"
                    placeholder="Type your professional response here..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Your reply will be sent to {selectedContact.strEmail || 'unknown email'}
                  </p>
                </div>
                
                <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                  <Button
                    onClick={() => {
                      setShowReplyModal(false);
                      setReplyMessage('');
                    }}
                    className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors duration-200"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReply}
                    disabled={!replyMessage.trim() || replying}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 ${
                      !replyMessage.trim() || replying
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    {replying ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Reply className="h-4 w-4 mr-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement; 